// Iter 82.5 / Phase E2.1 — Employee Self-Service "My Attendance".
// Big check-in / check-out / break / idle buttons reflecting live state.
// Today's KPIs (gross / net / breaks / idle / status). Today's event
// timeline. Last-30-day mini-calendar.
// Geo capture: when the browser provides geolocation, we send
// {lat, lng, accuracy} on every event (operator Decision 3A —
// future-ready, no enforcement in E2.1).
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Clock, ArrowsClockwise, Coffee, Pause, Play, SignIn, SignOut,
    Calendar, Buildings, MapPin, Warning, FileText,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate, formatTime } from "../../lib/format";
import ThreeMonthAttendancePanel from "./EssMyAttendance.threeMonth";
import MonthSummaryCard from "../../components/regularisation/MonthSummaryCard";

const STATUS_BADGE_CLS = {
    "Present": "bg-emerald-100 text-emerald-700",
    "Half Day": "bg-amber-100 text-amber-700",
    "Absent": "bg-rose-100 text-rose-700",
    "On Leave": "bg-sky-100 text-sky-700",
    "Holiday": "bg-indigo-100 text-indigo-700",
    "Weekly Off": "bg-slate-100 text-slate-600",
    "Late": "bg-orange-100 text-orange-700",
    "Early Departure": "bg-orange-100 text-orange-700",
    "Regularized": "bg-violet-100 text-violet-700",
    "Pending Regularization": "bg-yellow-100 text-yellow-700",
};

function formatMinutes(mins) {
    if (mins == null) return "—";
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h ${m}m`;
}

// Iter 102.2 · Attendance Timezone Cleanup — the previous local `formatTime`
// helper has been removed. All attendance timestamps are now rendered via
// the centralised `formatTime` / `formatTimeIST` in `/app/frontend/src/lib/
// format.js`, which forces the `Asia/Kolkata` timezone. See § J.4 governance
// rule in `NAVIGATION_RECONCILIATION_REPORT.md` for the swap point.

// Get the browser geolocation (best-effort, non-blocking).
async function captureGeo() {
    if (!("geolocation" in navigator)) return null;
    return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                accuracy_meters: pos.coords.accuracy,
            }),
            () => resolve(null),
            { enableHighAccuracy: false, timeout: 4000, maximumAge: 60000 },
        );
    });
}

export default function EssMyAttendance() {
    const navigate = useNavigate();
    const [today, setToday] = useState(null);
    const [loading, setLoading] = useState(true);
    const [breakOpen, setBreakOpen] = useState(false);
    const [breakName, setBreakName] = useState("");
    const [acting, setActing] = useState(false);

    // 2026-08-05 · Custom date-range for the calendar strip.  Defaults
    // to the same rolling-30-day window that /ess/today returns, but
    // the user can pick any range (up to a year) to inspect earlier
    // periods that the rolling window has already truncated.
    const isoDaysAgo = (n) => {
        const d = new Date(); d.setDate(d.getDate() - n);
        return d.toISOString().slice(0, 10);
    };
    const todayIso = () => new Date().toISOString().slice(0, 10);
    const [historyFrom, setHistoryFrom] = useState(isoDaysAgo(30));
    const [historyTo, setHistoryTo] = useState(todayIso());
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    // Iter 103.F · explicit refresh-key propagated to the 3-month
    // panel. Incremented ONLY after a successful check-in / -out /
    // break / idle action.  No global event bus.
    const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/ess/today");
            setToday(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load today's attendance"));
        } finally {
            setLoading(false);
        }
    }, []);

    const reloadHistory = useCallback(async (from, to) => {
        if (!from || !to) return;
        setHistoryLoading(true);
        try {
            const { data } = await api.get("/attendance/ess/history", {
                params: { from_date: from, to_date: to },
            });
            setHistory(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load attendance history"));
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    useEffect(() => { Promise.resolve().then(() => { reload(); }); }, [reload]);
    useEffect(() => { reloadHistory(historyFrom, historyTo); }, [historyFrom, historyTo, reloadHistory]);

    const doAction = async (action, extra = {}) => {
        if (acting) return;
        setActing(true);
        try {
            const geo = await captureGeo();
            const body = { action, ...extra };
            if (geo) body.geo = geo;
            await api.post("/attendance/ess/action", body);
            const labelMap = {
                check_in: "Checked in.", check_out: "Checked out.",
                break_start: "Break started.", break_end: "Break ended.",
                idle_start: "Marked idle.", idle_end: "Resumed from idle.",
            };
            toast.success(labelMap[action] || "Recorded.");
            await reload();
            // Only bump on success — the 3-month panel re-fetches.
            setSummaryRefreshKey((k) => k + 1);
        } catch (err) {
            toast.error(formatApiError(err, "Action failed"));
        } finally {
            setActing(false);
        }
    };

    const startBreak = async () => {
        if (!breakName.trim()) {
            toast.error("Please enter a break name (e.g., Lunch, Tea, Personal).");
            return;
        }
        await doAction("break_start", { break_name: breakName.trim() });
        setBreakOpen(false);
        setBreakName("");
    };

    if (loading) {
        return (
            <div className="px-6 sm:px-8 py-12 text-center text-slate-400" data-testid="ess-attendance-loading">
                Loading your attendance…
            </div>
        );
    }

    if (!today?.linked) {
        return (
            <div className="px-6 sm:px-8 py-6 max-w-3xl" data-testid="ess-attendance-unlinked">
                <PageHeader eyebrow="My Daily Attendance" title="Account not linked" description={today?.reason || "Ask HR to link your user account to your employee record."} />
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800 flex gap-2 items-start">
                    <Warning size={18} className="shrink-0 mt-0.5" />
                    <div>Your account is not yet linked to an employee record. Until HR completes the link, attendance check-in / check-out is unavailable.</div>
                </div>
            </div>
        );
    }

    const { employee, shift, snapshot: snap, events, state } = today;

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1200px]" data-testid="ess-attendance-page">
            <PageHeader
                eyebrow={`My Daily Attendance · ${employee.employee_code || ""}`}
                title={`Hello, ${employee.full_name?.split(" ")[0] || "there"}`}
                description={`Today is ${formatDate(today.today)}. ${shift ? `Your shift today: ${shift.name} (${shift.start_time} → ${shift.end_time}).` : "No shift assigned — ask HR."}`}
            />

            {/* Big action panel */}
            <MonthSummaryCard />
            <section className="mt-6 bg-white rounded-xl border border-slate-200 p-5" data-testid="ess-action-panel">
                <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`h-3 w-3 rounded-full ${state.checked_in ? "bg-emerald-500" : "bg-slate-300"}`} />
                        <div>
                            <div className="text-xs uppercase tracking-wider text-slate-500">Current State</div>
                            <div className="text-base font-semibold text-slate-800" data-testid="ess-state-text">
                                {state.on_break ? "On Break" : state.on_idle ? "Idle" : state.checked_in ? "Checked In" : "Checked Out"}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        {!state.checked_in && (
                            <Button onClick={() => doAction("check_in")} disabled={acting} data-testid="ess-check-in-btn" className="bg-emerald-600 hover:bg-emerald-700">
                                <SignIn size={16} className="mr-1.5" /> Check In
                            </Button>
                        )}
                        {state.checked_in && !state.on_break && !state.on_idle && (
                            <>
                                <Button variant="outline" onClick={() => setBreakOpen(true)} disabled={acting} data-testid="ess-break-start-btn">
                                    <Coffee size={16} className="mr-1.5" /> Start Break
                                </Button>
                                <Button variant="outline" onClick={() => doAction("idle_start")} disabled={acting} data-testid="ess-idle-start-btn">
                                    <Pause size={16} className="mr-1.5" /> Mark Idle
                                </Button>
                                <Button onClick={() => doAction("check_out")} disabled={acting} data-testid="ess-check-out-btn" className="bg-rose-600 hover:bg-rose-700">
                                    <SignOut size={16} className="mr-1.5" /> Check Out
                                </Button>
                            </>
                        )}
                        {state.on_break && (
                            <Button onClick={() => doAction("break_end")} disabled={acting} data-testid="ess-break-end-btn" className="bg-amber-600 hover:bg-amber-700">
                                <Play size={16} className="mr-1.5" /> End Break
                            </Button>
                        )}
                        {state.on_idle && (
                            <Button onClick={() => doAction("idle_end")} disabled={acting} data-testid="ess-idle-end-btn" className="bg-indigo-600 hover:bg-indigo-700">
                                <Play size={16} className="mr-1.5" /> Resume
                            </Button>
                        )}
                    </div>
                </div>
            </section>

            {/* KPI grid */}
            <section className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3" data-testid="ess-kpis">
                <Kpi label="Status" value={snap.attendance_status} variant={STATUS_BADGE_CLS[snap.attendance_status]} testId="kpi-status" />
                <Kpi label="Check-In" value={formatTime(snap.check_in_time)} testId="kpi-checkin" />
                <Kpi label="Check-Out" value={formatTime(snap.check_out_time)} testId="kpi-checkout" />
                <Kpi label="Gross Working" value={formatMinutes(snap.gross_working_minutes)} testId="kpi-gross" />
                <Kpi label="Net Working" value={formatMinutes(snap.net_working_minutes)} testId="kpi-net" />
                <Kpi label="Breaks · Idle" value={`${formatMinutes(snap.total_break_minutes)} · ${formatMinutes(snap.total_idle_minutes)}`} testId="kpi-breaks-idle" />
                {snap.is_late && <Kpi label="Late by" value={`${snap.late_minutes} min`} variant="bg-orange-100 text-orange-700" testId="kpi-late" />}
                {snap.is_early_departure && <Kpi label="Early left by" value={`${snap.early_departure_minutes} min`} variant="bg-orange-100 text-orange-700" testId="kpi-early" />}
                {snap.attendance_source && <Kpi label="Source" value={snap.attendance_source} testId="kpi-source" />}
                {/* Iter 98 / Phase E2.4 — agent active/idle/locked for WFH employees. */}
                {snap.attendance_source_mode && snap.attendance_source_mode !== "manual" && (
                    <>
                        <Kpi label="Agent Active" value={formatMinutes(snap.agent_total_active_minutes)} variant="bg-violet-100 text-violet-800" testId="kpi-agent-active" />
                        <Kpi label="Agent Idle · Locked" value={`${formatMinutes(snap.agent_total_idle_minutes)} · ${formatMinutes(snap.agent_total_locked_minutes)}`} variant="bg-violet-50 text-violet-700" testId="kpi-agent-idle-locked" />
                    </>
                )}
                {snap.needs_review && <Kpi label="Needs Review" value={snap.needs_review_reason || "—"} variant="bg-amber-100 text-amber-800" testId="kpi-needs-review" />}
            </section>

            {/* Iter 103.F · 3-Month My Attendance analytics panel */}
            <ThreeMonthAttendancePanel refreshKey={summaryRefreshKey} />

            {/* Iter 98 / Phase E2.4 — Bridged-attendance info banner. */}
            {snap.attendance_source_mode && snap.attendance_source_mode !== "manual" && (
                <div className="mt-3 bg-violet-50 border border-violet-200 rounded-md px-3 py-2 text-xs text-violet-800" data-testid="ess-bridge-banner">
                    <strong>Auto-attendance:</strong> Your attendance is being marked from the Desktop Agent {snap.attendance_source_mode === "hybrid" && <>(hybrid mode — manual check-in is also allowed)</>}. First active: <span className="font-mono">{formatTime(snap.agent_first_active_at)}</span> · Last active: <span className="font-mono">{formatTime(snap.agent_last_active_at)}</span>
                    {(snap.agent_long_gap_count || 0) > 0 && <> · <span className="text-rose-700 font-semibold">{snap.agent_long_gap_count} long offline gap(s) detected.</span></>}
                </div>
            )}

            {/* Today's event timeline */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-5" data-testid="ess-timeline">
                <h3 className="text-sm font-semibold mb-3">Today&apos;s Activity</h3>
                {events.length === 0 && (
                    <div className="py-8 text-center text-slate-400 text-sm">No events yet today. Click Check In to begin.</div>
                )}
                <ul className="space-y-2">
                    {events.map((e) => (
                        <li key={e.id} className="flex items-center gap-3 text-sm" data-testid={`event-row-${e.id}`}>
                            <span className="font-mono text-xs text-slate-500 w-16">{formatTime(e.event_time)}</span>
                            <EventBadge type={e.event_type} />
                            <span className="text-slate-700 flex-1 truncate">
                                {e.break_name ? `${e.break_name}` : e.remarks || ""}
                            </span>
                            <Badge variant="outline" className="text-[10px]">{e.source}</Badge>
                            {e.geo && <MapPin size={12} className="text-slate-400" title={`${e.geo.latitude.toFixed(4)},${e.geo.longitude.toFixed(4)}`} />}
                        </li>
                    ))}
                </ul>
            </section>

            {/* Attendance history · custom date range · defaults to last 30 days */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-5" data-testid="ess-30day">
                <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
                    <h3 className="text-sm font-semibold flex items-center gap-1.5">
                        <Calendar size={14} /> Attendance
                        {historyLoading && <span className="text-[10px] text-slate-400 font-normal">loading…</span>}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Range picker */}
                        <div className="flex items-center gap-1.5">
                            <Label htmlFor="hist-from" className="text-xs text-slate-500">From</Label>
                            <Input
                                id="hist-from"
                                type="date"
                                value={historyFrom}
                                max={historyTo || todayIso()}
                                onChange={(e) => setHistoryFrom(e.target.value)}
                                className="h-8 w-[140px] text-xs"
                                data-testid="ess-history-from"
                            />
                            <Label htmlFor="hist-to" className="text-xs text-slate-500">To</Label>
                            <Input
                                id="hist-to"
                                type="date"
                                value={historyTo}
                                min={historyFrom}
                                max={todayIso()}
                                onChange={(e) => setHistoryTo(e.target.value)}
                                className="h-8 w-[140px] text-xs"
                                data-testid="ess-history-to"
                            />
                        </div>
                        {/* Quick presets */}
                        <div className="flex items-center gap-1">
                            {[
                                { label: "Last 30d", from: isoDaysAgo(30), to: todayIso() },
                                { label: "This month", from: todayIso().slice(0, 7) + "-01", to: todayIso() },
                                { label: "Last month", from: (() => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); })(), to: (() => { const d = new Date(); d.setDate(0); return d.toISOString().slice(0, 10); })() },
                            ].map((p) => (
                                <button
                                    key={p.label}
                                    type="button"
                                    onClick={() => { setHistoryFrom(p.from); setHistoryTo(p.to); }}
                                    className="text-[11px] px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700"
                                    data-testid={`ess-history-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <Button size="sm" variant="outline" onClick={() => navigate("/regularization/my")} data-testid="ess-my-rgz-link"><FileText size={12} className="mr-1" /> My Regularisation Requests</Button>
                    </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {history.length === 0 && !historyLoading && <span className="text-xs text-slate-400">No snapshots in this range.</span>}
                    {history.map((d) => {
                        const flagged = d.is_late || d.is_early_departure || ["Absent", "Half Day"].includes(d.attendance_status);
                        return (
                            <div key={d.attendance_date} className="relative group" title={`${d.attendance_date} · ${d.attendance_status} · ${formatMinutes(d.net_working_minutes)}`}>
                                <div className={`h-9 w-9 rounded-md flex items-center justify-center text-[10px] font-medium ${STATUS_BADGE_CLS[d.attendance_status] || "bg-slate-100 text-slate-500"}`}>
                                    {d.attendance_date.slice(8, 10)}
                                </div>
                                {(d.is_late || d.is_early_departure) && (
                                    <div className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-orange-500" />
                                )}
                                {flagged && (
                                    <button
                                        onClick={() => navigate(`/regularization/new?date=${d.attendance_date}`)}
                                        className="absolute inset-0 rounded-md opacity-0 group-hover:opacity-100 bg-slate-900/80 text-white text-[9px] font-medium transition-opacity"
                                        title="Regularize this day"
                                        data-testid={`regularize-${d.attendance_date}`}
                                    >
                                        ✎ Fix
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
                <p className="text-[11px] text-slate-500 mt-3">Hover any flagged day to &quot;Fix&quot; via regularization. Orange dot = Late / Early Departure flag. Showing {history.length} day{history.length === 1 ? "" : "s"} from {historyFrom} to {historyTo}.</p>
            </section>

            {/* Break dialog */}
            <Dialog open={breakOpen} onOpenChange={(o) => { if (!o) setBreakOpen(false); }}>
                <DialogContent data-testid="ess-break-dialog">
                    <DialogHeader>
                        <DialogTitle>Start a Break</DialogTitle>
                        <DialogDescription>Track the kind of break you&apos;re taking (Lunch, Tea, Personal, Doctor&apos;s appointment, etc.) for audit + analytics.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Break name</Label>
                            <Input data-testid="ess-break-name-input" value={breakName} onChange={(e) => setBreakName(e.target.value)} placeholder="Lunch" autoFocus />
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {["Lunch", "Tea", "Personal", "Doctor's Appointment", "Client Visit"].map((q) => (
                                <button key={q} type="button" onClick={() => setBreakName(q)} className="text-xs px-2 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700">{q}</button>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBreakOpen(false)}>Cancel</Button>
                        <Button onClick={startBreak} data-testid="ess-break-confirm-btn">Start</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Kpi({ label, value, variant, testId }) {
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            {variant ? (
                <Badge className={`mt-1 ${variant}`}>{value}</Badge>
            ) : (
                <div className="mt-0.5 text-sm font-semibold text-slate-800">{value}</div>
            )}
        </div>
    );
}

function EventBadge({ type }) {
    const cls = {
        check_in: "bg-emerald-100 text-emerald-700",
        check_out: "bg-rose-100 text-rose-700",
        break_start: "bg-amber-100 text-amber-700",
        break_end: "bg-amber-100 text-amber-700",
        idle_start: "bg-indigo-100 text-indigo-700",
        idle_end: "bg-indigo-100 text-indigo-700",
        login: "bg-slate-100 text-slate-600",
        logout: "bg-slate-100 text-slate-600",
    }[type] || "bg-slate-100 text-slate-600";
    return <Badge className={`text-[10px] ${cls}`}>{type.replace("_", " ")}</Badge>;
}
